/*
 * A booking's texts and emails (pages/schedule/PatientMessagesPanel.tsx, PA-7), in Ice Blue. The
 * panel sits in Schedule's dialog and in Attention's right panel, so it declares its own --pm-*
 * tokens (the canonical Ice Blue values, docs/UI-GUIDE.md); their dark values are in
 * appearance.css. Its buttons are .pm-btn (the .ad-btn shape) so they never depend on the host.
 */
.pm {
  --pm-ink: #111827;
  --pm-muted: #606d80;
  --pm-line: #e1ecf8;
  --pm-line-strong: #cde1f6;
  --pm-panel: #ffffff;
  --pm-panel-2: #f6fafe;
  --pm-wash: #f0f7fe;
  --pm-ring: #e3f0fd;
  --pm-accent: #3d8bf5;
  --pm-accent-soft: #79b4f8;
  --pm-blue: #2468cc;
  --pm-btn-fill: #2468cc;
  --pm-btn-fill-hover: #1b4f9e;
  --pm-btn-ink: #ffffff;
  --pm-warn-ink: #6b4b12;
  --pm-warn-wash: #f6e8c8;
  --pm-warn-line: #ead6a8;
  --pm-quiet-wash: #f0f2f6;
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: var(--pm-ink);
  font-family: "Inter", "SF Pro Text", "Segoe UI", system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.5;
}
.pm *,
.pm *::before,
.pm *::after {
  box-sizing: border-box;
}
.pm :is(button, input) {
  font-family: inherit;
}
.pm-head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
}
.pm-head h3,
.pm-contact h4 {
  margin: 0;
  color: var(--pm-ink);
  font-size: 14px;
  font-weight: 600;
}
.pm-sub,
.pm-quiet,
.pm-row-meta,
.pm-field-hint {
  margin: 0;
  color: var(--pm-muted);
  font-size: 12.5px;
}
.pm-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: 1px solid var(--pm-line);
  border-radius: 14px;
  background: var(--pm-panel);
  list-style: none;
}
.pm-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
}
.pm-row + .pm-row {
  border-top: 1px solid var(--pm-line);
}
.pm-row-main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 8px;
}
.pm-row-title {
  font-weight: 600;
}
.pm-spacer {
  flex: 1 1 auto;
}
.pm-row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
}
.pm-row-why {
  margin: 0;
  color: var(--pm-ink);
  font-size: 12.5px;
}
.pm-chip {
  display: inline-flex;
  align-items: center;
  padding: 1px 7px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.6;
}
.pm-chip.tone-attention {
  background: var(--pm-warn-wash);
  color: var(--pm-warn-ink);
}
.pm-chip.tone-done {
  background: var(--pm-ring);
  color: var(--pm-blue);
}
.pm-chip.tone-progress {
  background: var(--pm-wash);
  color: var(--pm-blue);
}
.pm-chip.tone-muted {
  background: var(--pm-quiet-wash);
  color: var(--pm-muted);
}
.pm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 6px 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition:
    background 120ms ease,
    border-color 120ms ease;
}
.pm-btn.sm {
  min-height: 30px;
  padding: 4px 11px;
  border-radius: 8px;
  font-size: 12.5px;
}
.pm-btn.primary {
  border-color: var(--pm-btn-fill);
  background: var(--pm-btn-fill);
  color: var(--pm-btn-ink);
}
.pm-btn.primary:hover:not(:disabled) {
  border-color: var(--pm-btn-fill-hover);
  background: var(--pm-btn-fill-hover);
}
.pm-btn.ghost {
  border-color: var(--pm-line-strong);
  background: var(--pm-panel);
  color: var(--pm-ink);
}
.pm-btn.ghost:hover:not(:disabled) {
  background: var(--pm-wash);
}
.pm-btn:disabled {
  border-color: var(--pm-line);
  background: var(--pm-wash);
  color: var(--pm-muted);
  cursor: not-allowed;
}
.pm :is(button, input):focus-visible {
  outline: 2px solid var(--pm-accent);
  outline-offset: 2px;
}
/* A row's outcome note takes focus when the button that was pressed leaves the row. */
.pm :is(.pm-row-note, .pm-row-error)[tabindex]:focus-visible {
  outline: 2px solid var(--pm-accent);
  outline-offset: 2px;
  border-radius: 6px;
}
.pm-confirm {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
  padding: 10px 12px;
  border: 1px solid var(--pm-line-strong);
  border-radius: 10px;
  background: var(--pm-panel-2);
}
.pm-confirm p {
  margin: 0;
}
.pm-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
.pm-row-error,
.pm-error {
  margin: 4px 0 0;
  padding: 8px 10px;
  border: 1px solid var(--pm-warn-line);
  border-radius: 10px;
  background: var(--pm-warn-wash);
  color: var(--pm-warn-ink);
  font-size: 12.5px;
  font-weight: 500;
}
.pm-error {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.pm-error p {
  margin: 0;
}
.pm-row-note {
  margin: 4px 0 0;
  color: var(--pm-blue);
  font-size: 12.5px;
  font-weight: 500;
}
.pm-contact {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border: 1px solid var(--pm-line);
  border-radius: 14px;
  background: var(--pm-panel-2);
}
.pm-contact-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}
.pm-contact-form > :is(.pm-field-hint, .pm-row-error, .pm-actions) {
  grid-column: 1 / -1;
}
.pm-field {
  display: grid;
  gap: 5px;
  color: var(--pm-muted);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.pm-field input {
  width: 100%;
  min-height: 36px;
  padding: 7px 9px;
  border: 1px solid var(--pm-line-strong);
  border-radius: 8px;
  background: var(--pm-panel);
  color: var(--pm-ink);
  font-size: 13px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
}
.pm-field input:focus {
  outline: none;
  border-color: var(--pm-accent-soft);
  box-shadow: 0 0 0 3px var(--pm-ring);
}
/*
 * A patient's agreement to texts and emails (MessagingConsentPanel, PA-8): one row per channel and
 * the desk's "Patient agreed" / "Patient said no" form on the panel. Also on the chart's Consent
 * card and in Today's walk-in dialog, so it uses only these tokens.
 */
.pm-consent h4 {
  margin: 0;
  color: var(--pm-ink);
  font-size: 14px;
  font-weight: 600;
}
.pm-consent-form {
  gap: 10px;
}
.pm-consent-choices {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 16px;
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}
.pm-consent-choices legend {
  width: 100%;
  margin: 0 0 4px;
  padding: 0;
  color: var(--pm-muted);
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.pm-check {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--pm-ink);
  font-size: 12.5px;
  font-weight: 500;
}
.pm-check input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: var(--pm-btn-fill);
}
.pm-check-hint {
  color: var(--pm-muted);
  font-weight: 400;
}
.pm-consent-notice {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.pm-consent-text {
  padding: 8px 10px;
  border: 1px solid var(--pm-line);
  border-radius: 10px;
  background: var(--pm-panel);
}
.pm-consent-text summary {
  color: var(--pm-blue);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.pm-consent-text summary:focus-visible {
  outline: 2px solid var(--pm-accent);
  outline-offset: 2px;
  border-radius: 6px;
}
.pm-consent-text[open] summary {
  margin-bottom: 8px;
}
/* The notice scrolls in its own region, which takes keyboard focus so it can be scrolled. */
.pm-consent-text .pm-consent-scroll {
  max-height: 280px;
  overflow-y: auto;
  border-radius: 6px;
}
.pm-consent-text .pm-consent-scroll:focus-visible {
  outline: 2px solid var(--pm-accent);
  outline-offset: 2px;
}
.pm-consent-text .consent-text {
  font-size: 12.5px;
}
.pm-row-error .pm-btn {
  margin-left: 6px;
}
/* Schedule's "Texts and emails" dialog: narrower than the wide review dialogs. */
.schedule-modal.pm-dialog {
  width: min(760px, 100%);
}
@media (max-width: 620px) {
  .pm-contact-form {
    grid-template-columns: 1fr;
  }
}
